// Remappable controls: action table, defaults, labels, conflict-aware rebinding and a versioned save.
// Pure logic (no DOM, no three): Input applies the resolved maps, main.ts draws the Controls screen.
//
// Hard-wired fallbacks that can never be rebound or removed, so a bad mapping can't lock anyone out:
//   controller: Start/OPTIONS = pause (and back in menus), D-pad + move stick = menu navigation
//   keyboard:   Esc = pause/back, Enter = confirm, arrow keys = move + menu navigation

export type BindCtx = 'any' | 'run' | 'menu' | 'draft';
export type ActionId =
  | 'pause' | 'minimap' | 'mute' | 'perf'
  | 'moveUp' | 'moveDown' | 'moveLeft' | 'moveRight' | 'surge' | 'camera' | 'aimMode'
  | 'confirm' | 'back' | 'tabPrev' | 'tabNext'
  | 'reroll' | 'ban' | 'pick1' | 'pick2' | 'pick3';
export type Device = 'pad' | 'key';
export type PadFamilyName = 'playstation' | 'xbox' | 'generic';

export interface ActionDef {
  id: ActionId;
  label: string;
  ctx: BindCtx;
  /** default controller buttons (W3C standard indices) */
  pad: number[];
  /** default keyboard codes (KeyboardEvent.code) */
  keys: string[];
  /** controller column can't be rebound (fixed input shown instead, or none) */
  padFixed?: boolean;
  keyFixed?: boolean;
  /** shown next to the binding, always active, never removable (e.g. 'Esc') */
  padExtra?: string;
  keyExtra?: string;
  /** must keep at least one controller button (menu confirm / back) */
  padRequired?: boolean;
}

export const STICK_MOVE = 'move stick';
export const ACTIONS: readonly ActionDef[] = [
  // ---- in a run ----
  { id: 'moveUp', label: 'Move up', ctx: 'run', pad: [], keys: ['KeyW'], padFixed: true, keyExtra: '↑' },
  { id: 'moveDown', label: 'Move down', ctx: 'run', pad: [], keys: ['KeyS'], padFixed: true, keyExtra: '↓' },
  { id: 'moveLeft', label: 'Move left', ctx: 'run', pad: [], keys: ['KeyA'], padFixed: true, keyExtra: '←' },
  { id: 'moveRight', label: 'Move right', ctx: 'run', pad: [], keys: ['KeyD'], padFixed: true, keyExtra: '→' },
  { id: 'surge', label: 'Surge Form', ctx: 'run', pad: [3], keys: ['KeyR', 'KeyK'] },
  // owner 2026-11-02: Nova Lance is always automatic, so there is no manual 'fire now' control any more
  { id: 'camera', label: 'Camera distance (cycle)', ctx: 'run', pad: [8], keys: ['KeyV'] },
  { id: 'aimMode', label: 'Aim mode', ctx: 'run', pad: [11], keys: ['KeyT'] },
  // ---- anywhere ----
  { id: 'pause', label: 'Pause', ctx: 'any', pad: [], keys: ['KeyP'], padFixed: true, padExtra: 'start', keyExtra: 'Esc' },
  { id: 'minimap', label: 'Minimap on / off', ctx: 'any', pad: [7], keys: ['KeyN'] },
  { id: 'mute', label: 'Mute sound', ctx: 'any', pad: [], keys: ['KeyM'] },
  { id: 'perf', label: 'Performance overlay', ctx: 'any', pad: [], keys: ['F3', 'Backquote'] },
  // ---- menus ----
  { id: 'confirm', label: 'Confirm', ctx: 'menu', pad: [0], keys: [], keyExtra: 'Enter / Space', padRequired: true },
  { id: 'back', label: 'Back', ctx: 'menu', pad: [1], keys: [], padExtra: 'start', keyExtra: 'Esc', padRequired: true },
  // owner 2026-11-02: LB / RB flip between the Settings tabs
  { id: 'tabPrev', label: 'Previous tab', ctx: 'menu', pad: [4], keys: ['BracketLeft', 'PageUp'] },
  { id: 'tabNext', label: 'Next tab', ctx: 'menu', pad: [5], keys: ['BracketRight', 'PageDown'] },
  // ---- level-up draft ----
  { id: 'reroll', label: 'Reroll cards', ctx: 'draft', pad: [2], keys: ['KeyQ'] },
  { id: 'ban', label: 'Ban a card', ctx: 'draft', pad: [3], keys: ['KeyB'] },
  { id: 'pick1', label: 'Pick card 1', ctx: 'draft', pad: [], keys: ['Digit1'] },
  { id: 'pick2', label: 'Pick card 2', ctx: 'draft', pad: [], keys: ['Digit2'] },
  { id: 'pick3', label: 'Pick card 3', ctx: 'draft', pad: [], keys: ['Digit3'] },
];
export const ACTION: Record<ActionId, ActionDef> = Object.fromEntries(ACTIONS.map((a) => [a.id, a])) as Record<ActionId, ActionDef>;
export const CTX_LABEL: Record<BindCtx, string> = { run: 'IN A RUN', any: 'ANYWHERE', menu: 'MENUS', draft: 'LEVEL-UP CARDS' };

/** Two contexts clash when both can be live on the same screen. */
export function overlaps(a: BindCtx, b: BindCtx): boolean {
  if (a === 'any' || b === 'any' || a === b) return true;
  return (a === 'menu' && b === 'draft') || (a === 'draft' && b === 'menu');
}

/** Hard-wired buttons/keys that can't be assigned. Start cancels a capture; the D-pad always navigates menus
 *  (it may still drive in-run actions, where menus aren't open). */
export const PAD_START = 9;
export const PAD_DPAD = [12, 13, 14, 15];
export const RESERVED_KEYS = ['Escape', 'Enter', 'NumpadEnter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'F5', 'F11', 'F12', 'MetaLeft', 'MetaRight'];
export function isReserved(action: ActionId, dev: Device, code: number | string): boolean {
  if (dev === 'pad') {
    const i = code as number;
    if (!Number.isInteger(i) || i < 0 || i > 16 || i === PAD_START) return true;
    return PAD_DPAD.includes(i) && ACTION[action].ctx !== 'run';
  }
  return typeof code !== 'string' || !code || RESERVED_KEYS.includes(code);
}

// ---------------- profile (what gets saved) ----------------
export const BIND_VERSION = 1;
export const BIND_KEY = 'arena.bindings';
export interface PadOptions {
  /** stick dead zone, one of DEADZONES */
  deadzone: number;
  invertAimY: boolean;
  /** move on the right stick, aim on the left */
  swapSticks: boolean;
}
export interface BindingProfile { v: number; pad: Record<ActionId, number[]>; keys: Record<ActionId, string[]>; opts: PadOptions }
export const DEFAULT_OPTS: PadOptions = { deadzone: 0.18, invertAimY: false, swapSticks: false };
export const DEADZONES = [0.1, 0.18, 0.25, 0.35];

export function defaultProfile(): BindingProfile {
  const pad = {} as Record<ActionId, number[]>, keys = {} as Record<ActionId, string[]>;
  for (const a of ACTIONS) { pad[a.id] = [...a.pad]; keys[a.id] = [...a.keys]; }
  return { v: BIND_VERSION, pad, keys, opts: { ...DEFAULT_OPTS } };
}

/** All bindings that clash (same button/key, overlapping contexts). */
export function conflicts(p: BindingProfile, dev: Device): [ActionId, ActionId, number | string][] {
  const out: [ActionId, ActionId, number | string][] = [];
  const m = dev === 'pad' ? p.pad : p.keys;
  for (let i = 0; i < ACTIONS.length; i++) for (let j = i + 1; j < ACTIONS.length; j++) {
    const a = ACTIONS[i], b = ACTIONS[j];
    if (!overlaps(a.ctx, b.ctx)) continue;
    for (const c of m[a.id] as (number | string)[]) if ((m[b.id] as (number | string)[]).includes(c)) out.push([a.id, b.id, c]);
  }
  return out;
}

/** Upgrade / repair a stored profile. Anything unknown, invalid, reserved or clashing falls back to the default for that
 *  action; actions added after the save was written get their defaults; a newer or unreadable save gives the defaults. */
export function migrateBindings(raw: unknown): BindingProfile {
  const d = defaultProfile();
  if (!raw || typeof raw !== 'object') return d;
  const r = raw as { v?: unknown; pad?: unknown; keys?: unknown; opts?: unknown };
  if (typeof r.v !== 'number' || r.v > BIND_VERSION || r.v < 1) return d;
  const p = defaultProfile();
  const padIn = (r.pad && typeof r.pad === 'object' ? r.pad : {}) as Record<string, unknown>;
  const keyIn = (r.keys && typeof r.keys === 'object' ? r.keys : {}) as Record<string, unknown>;
  for (const a of ACTIONS) {
    const pv = padIn[a.id];
    if (!a.padFixed && Array.isArray(pv) && pv.length <= 4 && pv.every((x) => typeof x === 'number' && !isReserved(a.id, 'pad', x))) {
      if (!(a.padRequired && pv.length === 0)) p.pad[a.id] = [...new Set(pv as number[])];
    }
    const kv = keyIn[a.id];
    if (!a.keyFixed && Array.isArray(kv) && kv.length <= 4 && kv.every((x) => typeof x === 'string' && !isReserved(a.id, 'key', x))) p.keys[a.id] = [...new Set(kv as string[])];
  }
  // clashes (hand-edited or from an older table): put the clashing actions back to their defaults, else give up
  for (const dev of ['pad', 'key'] as Device[]) {
    const m = dev === 'pad' ? p.pad : p.keys, dm = dev === 'pad' ? d.pad : d.keys;
    for (const [a, b] of conflicts(p, dev)) { (m as Record<ActionId, unknown[]>)[a] = [...dm[a]]; (m as Record<ActionId, unknown[]>)[b] = [...dm[b]]; }
    if (conflicts(p, dev).length) { if (dev === 'pad') p.pad = d.pad; else p.keys = d.keys; }
  }
  const o = (r.opts && typeof r.opts === 'object' ? r.opts : {}) as Partial<PadOptions>;
  p.opts = {
    deadzone: typeof o.deadzone === 'number' && DEADZONES.includes(o.deadzone) ? o.deadzone : DEFAULT_OPTS.deadzone,
    invertAimY: typeof o.invertAimY === 'boolean' ? o.invertAimY : DEFAULT_OPTS.invertAimY,
    swapSticks: typeof o.swapSticks === 'boolean' ? o.swapSticks : DEFAULT_OPTS.swapSticks,
  };
  return p;
}

export interface KVLike { getItem(k: string): string | null; setItem(k: string, v: string): void }
export function loadBindings(kv: KVLike | null): BindingProfile {
  if (!kv) return defaultProfile();
  try { const s = kv.getItem(BIND_KEY); return s ? migrateBindings(JSON.parse(s)) : defaultProfile(); } catch { return defaultProfile(); }
}
export function storeBindings(kv: KVLike | null, p: BindingProfile): void {
  if (!kv) return;
  try { kv.setItem(BIND_KEY, JSON.stringify(p)); } catch { /* storage blocked: keep in memory */ }
}

// ---------------- rebinding ----------------
export type BindResult =
  | { ok: true; changed: boolean; swapped?: ActionId; unbound?: ActionId }
  | { ok: false; reason: 'fixed' | 'reserved' | 'required' };

/** Bind `code` as the only `dev` input for `action`. An overlapping action already using it takes over this action's
 *  previous input (a swap); if that isn't possible it's left unbound (reported), unless it must keep a button. */
export function rebind(p: BindingProfile, action: ActionId, dev: Device, code: number | string): BindResult {
  const def = ACTION[action];
  if (dev === 'pad' ? def.padFixed : def.keyFixed) return { ok: false, reason: 'fixed' };
  if (isReserved(action, dev, code)) return { ok: false, reason: 'reserved' };
  const m = (dev === 'pad' ? p.pad : p.keys) as Record<ActionId, (number | string)[]>;
  const old = m[action];
  if (old.length === 1 && old[0] === code) return { ok: true, changed: false };
  const other = ACTIONS.find((a) => a.id !== action && overlaps(a.ctx, def.ctx) && m[a.id].includes(code));
  let swapped: ActionId | undefined, unbound: ActionId | undefined;
  if (other) {
    const rest = m[other.id].filter((c) => c !== code);
    // swap candidates: this action's old inputs that the other action could legally take without a new clash
    const give = old.find((c) => c !== code && !isReserved(other.id, dev, c) && !ACTIONS.some((a) => a.id !== action && a.id !== other.id && overlaps(a.ctx, other.ctx) && m[a.id].includes(c)));
    const next = give !== undefined ? [...new Set([...rest, give])] : rest;
    if (next.length === 0 && dev === 'pad' && other.padRequired) return { ok: false, reason: 'required' };
    m[other.id] = next;
    if (give !== undefined) swapped = other.id; else if (next.length === 0) unbound = other.id;
  }
  m[action] = [code];
  return { ok: true, changed: true, swapped, unbound };
}

// ---------------- resolution (what Input uses every frame) ----------------
export interface Resolved { pad: Map<number, ActionId[]>; key: Map<string, ActionId[]>; opts: PadOptions }
export function resolve(p: BindingProfile): Resolved {
  const pad = new Map<number, ActionId[]>(), key = new Map<string, ActionId[]>();
  for (const a of ACTIONS) {
    for (const b of p.pad[a.id]) pad.set(b, [...(pad.get(b) ?? []), a.id]);
    for (const k of p.keys[a.id]) key.set(k, [...(key.get(k) ?? []), a.id]);
  }
  return { pad, key, opts: { ...p.opts } };
}

// ---------------- labels ----------------
const FACE: Record<PadFamilyName, string[]> = {
  playstation: ['✕', '○', '□', '△', 'L1', 'R1', 'L2', 'R2', 'CREATE', 'OPTIONS', 'L3', 'R3'],
  xbox: ['A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'View', 'Menu', 'LS', 'RS'],
  generic: ['A', 'B', 'X', 'Y', 'L1', 'R1', 'L2', 'R2', 'Select', 'Start', 'L3', 'R3'],
};
export function padLabel(i: number, fam: PadFamilyName): string {
  if (i >= 0 && i < 12) return FACE[fam][i];
  return ({ 12: 'D-pad ↑', 13: 'D-pad ↓', 14: 'D-pad ←', 15: 'D-pad →', 16: fam === 'playstation' ? 'PS' : 'Guide' } as Record<number, string>)[i] ?? `Button ${i}`;
}
const KEY_NAMES: Record<string, string> = {
  Space: 'Space', Backquote: '`', Minus: '-', Equal: '=', BracketLeft: '[', BracketRight: ']', Backslash: '\\', Semicolon: ';', Quote: "'",
  Comma: ',', Period: '.', Slash: '/', Backspace: 'Backspace', ShiftLeft: 'L-Shift', ShiftRight: 'R-Shift', ControlLeft: 'L-Ctrl', ControlRight: 'R-Ctrl',
  AltLeft: 'L-Alt', AltRight: 'R-Alt', CapsLock: 'Caps', Escape: 'Esc', Enter: 'Enter', Delete: 'Del', Insert: 'Ins', Home: 'Home', End: 'End', PageUp: 'PgUp', PageDown: 'PgDn',
};
export function keyLabel(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return 'Num ' + code.slice(6);
  if (/^F\d{1,2}$/.test(code)) return code;
  return KEY_NAMES[code] ?? code;
}
/** Display text of one cell of the Controls table. */
export function cellText(p: BindingProfile, action: ActionId, dev: Device, fam: PadFamilyName): string {
  const def = ACTION[action];
  if (dev === 'pad') {
    if (action.startsWith('move')) return `${p.opts.swapSticks ? 'Right' : 'Left'} stick`;
    const parts = p.pad[action].map((i) => padLabel(i, fam));
    if (def.padExtra) parts.push(padLabel(PAD_START, fam));
    return parts.join(' / ') || '—';
  }
  const parts = p.keys[action].map(keyLabel);
  if (def.keyExtra) parts.push(def.keyExtra);
  return parts.join(' / ') || '—';
}
